import http from './http-common'; // Ensure http is configured correctly with axios or your preferred HTTP client
import User from '../types/User'; // Import the User type
import LoginResponse from "../types/LoginResponse"

interface LoginRequest {
  email: string;
  password: string;
}

class UserLoginAPI {
  /**
   * Log in a user.
   * @param credentials - User email and password.
   * @returns LoginResponse or undefined if the request fails.
   */
  async login(credentials: LoginRequest): Promise<LoginResponse | undefined> {
    try {
      const response = await http.post<LoginResponse>("/login", credentials);

      // Save token and user details to localStorage
      localStorage.setItem("authToken", response.data.token);
      localStorage.setItem("currentUser", JSON.stringify(response.data.user));
      localStorage.setItem("userRoles", JSON.stringify(response.data.roles));


      return response.data;
    } catch (error) {
      this.handleError(error);
      return undefined; // Return undefined if login fails
    }
  }

  /**
   * Log out the current user.
   * @returns boolean indicating success.
   */
  async logout(): Promise<boolean> {
    try {
      // Send logout request to the server
      const response = await http.post("/logout");

      // Clear user data from localStorage
      localStorage.removeItem("authToken");
      localStorage.removeItem("currentUser");
      localStorage.removeItem("userRoles");

      // Log success message if server responds correctly
      console.log(response.data?.message || "User logged out successfully.");
      return true;
    } catch (error) {
      this.handleError(error);
      return false; // Return false if logout fails
    }
  }

  /**
   * Retrieve the current logged-in user from localStorage.
   * @returns The logged-in user's information or null if no user is logged in.
   */
  getCurrentUser(): User | null {
    try {
      const user = localStorage.getItem("currentUser");
      return user ? JSON.parse(user) : null;
    } catch (error) {
      console.error("Failed to retrieve current user: ", error);
      return null;
    }
  }

  /**
   * Retrieve roles of the current user from localStorage.
   * @returns An array of roles or an empty array if no roles are found.
   */
  getUserRoles(): string[] {
    try {
      const roles = localStorage.getItem("userRoles");
      return roles ? JSON.parse(roles) : [];
    } catch (error) {
      console.error("Failed to retrieve user roles: ", error);
      return [];
    }
  }

  /**
   * Handle API errors and log useful information.
   * @param error - The error object from the API call.
   */
  private handleError(error: any): void {
    console.error("API call failed: ", error);
    if (error.response) {
      console.error("HTTP Status: ", error.response.status);
      console.error("Response Data: ", error.response.data);
    } else if (error.request) {
      console.error("No response received: ", error.request);
    } else {
      console.error("Request setup error: ", error.message);
    }
  }
}

const userLoginAPIInstance = new UserLoginAPI();
export default userLoginAPIInstance;
